<template>
  <div class="bg-white modal-card rounded ai-document-modal">
    <div class="bg-primary-100 p-4 text-xl">
      Modifier le document
    </div>

    <div class="px-4 py-3 text-sm text-gray-600 border-b">
      <div class="ai-document-modal-title">{{ document.title || 'Document sans titre' }}</div>
      <div class="ai-document-modal-meta">
        <span>{{ global ? 'Tous les domaines (global)' : scopedDomainLabel }}</span>
        <span>{{ docTypeLabel(form.doc_type) }}</span>
        <span v-if="chunksCount !== null">{{ chunksCount }} fragment(s)</span>
      </div>
    </div>

    <div class="ai-document-tabs">
      <button
        type="button"
        :class="{ 'is-active': activeTab === 'metadata' }"
        @click="activeTab = 'metadata'"
      >
        Informations
      </button>
      <button
        type="button"
        :class="{ 'is-active': activeTab === 'text' }"
        @click="activeTab = 'text'"
      >
        Texte extrait
      </button>
    </div>

    <div v-show="activeTab === 'metadata'" class="ai-document-modal-body">
      <div class="px-4 py-2 text-sm text-gray-600 border-b flex items-center justify-between gap-4">
        <div>
          Disponibilité :
          <span class="font-semibold">{{ global ? 'Tous les domaines (global)' : scopedDomainLabel }}</span>
        </div>
        <label class="flex items-center gap-2 text-xs whitespace-nowrap cursor-pointer">
          <t-toggle v-model="global" />
          Tous les domaines
        </label>
      </div>

      <div class="grid grid-cols-2 gap-4 p-4">
        <t-input-group class="col-span-2" label="Titre du document">
          <t-input v-model="form.title" placeholder="Ex : Accord télétravail 2026" />
        </t-input-group>

        <t-input-group label="Type de document">
          <t-select v-model="form.doc_type" :options="docTypeOptions" />
        </t-input-group>

        <t-input-group label="Priorité">
          <t-input v-model.number="form.priority" type="number" min="0" max="1000" placeholder="100" />
        </t-input-group>

        <t-input-group label="Date de signature">
          <t-datepicker v-model="form.signed_at" :clearable="true" />
        </t-input-group>

        <t-input-group label="Valable jusqu'au">
          <t-datepicker v-model="form.valid_until" :clearable="true" />
        </t-input-group>

        <t-input-group class="col-span-2" label="URL source">
          <t-input v-model="form.source_url" type="url" placeholder="https://…" />
        </t-input-group>
      </div>
    </div>

    <div v-show="activeTab === 'text'" class="ai-document-modal-body">
      <div class="px-4 py-3 text-sm text-gray-600 border-b">
        <div class="ai-document-modal-meta">
          <span>{{ charCount }} caractère(s)</span>
          <span v-if="chunksCount !== null">{{ chunksCount }} fragment(s)</span>
          <span v-if="contentRevision !== null">Révision {{ contentRevision }}</span>
        </div>
      </div>

      <div class="p-4">
        <div v-if="loadingText" class="ai-text-loading">Chargement du texte extrait...</div>
        <template v-else>
          <div v-if="isTextLocked" class="ai-text-notice ai-text-notice--warning">
            L’indexation de ce document est en cours. Le texte sera modifiable dès qu’elle sera terminée.
          </div>

          <div v-if="revisionConflict" class="ai-text-notice ai-text-notice--danger">
            <span>Le texte a été modifié ailleurs depuis son chargement.</span>
            <button type="button" @click="reloadText">Recharger la dernière version</button>
          </div>

          <div class="ai-block-toolbar">
            <button
              type="button"
              class="ai-block-navigation"
              :disabled="currentBlockIndex === 0"
              @click="previousBlock"
            >
              <i class="fas fa-chevron-left" aria-hidden="true"></i>
              Précédent
            </button>

            <div class="ai-block-position">
              <strong>Bloc d’édition {{ currentBlockIndex + 1 }} sur {{ textBlocks.length }}</strong>
              <span v-if="currentPageNumber !== null">Page {{ currentPageNumber }} sur {{ pageCount }}</span>
              <span>{{ currentTextBlock.length }} caractère(s) dans ce bloc</span>
            </div>

            <button
              type="button"
              class="ai-block-navigation"
              :disabled="currentBlockIndex >= textBlocks.length - 1"
              @click="nextBlock"
            >
              Suivant
              <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>

          <p class="ai-block-help">
            Ces blocs ne se chevauchent pas. Ils seront recollés exactement dans cet ordre lors de l’enregistrement<span v-if="hasPageOffsets">, sans mélanger les pages du PDF</span>.
          </p>

          <div class="ai-block-actions">
            <button
              type="button"
              class="ai-block-delete"
              :disabled="!canDeleteCurrentUnit"
              :title="deleteCurrentUnitTitle"
              @click="confirmDeleteCurrentUnit"
            >
              <i class="fas fa-trash-alt" aria-hidden="true"></i>
              {{ deleteCurrentUnitLabel }}
            </button>
          </div>

          <div v-if="!pageOffsetsValid" class="ai-text-notice ai-text-notice--danger">
            Chaque page, sauf éventuellement la dernière, doit conserver au moins un caractère pour préserver la pagination.
          </div>

          <t-textarea
            v-model="currentTextBlock"
            class="ai-text-editor"
            rows="20"
            placeholder="Aucun texte extrait."
            :disabled="isTextLocked || revisionConflict"
          />
        </template>
      </div>
    </div>

    <div class="bg-gray-50 px-4 py-3 flex gap-3 justify-end">
      <t-button class="m-0" variant="secondary" :disabled="savingMetadata || savingText" @click="$parent.close()">
        Fermer
      </t-button>
      <t-button
        v-if="activeTab === 'metadata'"
        :disabled="savingMetadata || !canSaveMetadata"
        @click.prevent="saveMetadata"
      >
        {{ savingMetadata ? 'Enregistrement...' : 'Enregistrer' }}
      </t-button>
      <t-button
        v-else
        :disabled="loadingText || savingText || !canSaveText"
        @click.prevent="saveText"
      >
        {{ savingText ? 'Enregistrement...' : 'Enregistrer et reconstruire l’index' }}
      </t-button>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import aiService from '@/services/ai.service'

const DOC_TYPE_OPTIONS = [
  { value: 'autre', text: 'Autre' },
  { value: 'accord', text: "Accord d'entreprise" },
  { value: 'avenant', text: 'Avenant' },
  { value: 'ccn', text: 'Convention collective' },
  { value: 'code_travail', text: 'Code du travail' }
]

const EDITOR_BLOCK_TARGET_LENGTH = 5000

function utf8CharacterSize (text, index) {
  const firstCode = text.charCodeAt(index)
  if (firstCode <= 0x7F) {
    return { bytes: 1, codeUnits: 1 }
  }
  if (firstCode <= 0x7FF) {
    return { bytes: 2, codeUnits: 1 }
  }
  if (firstCode >= 0xD800 && firstCode <= 0xDBFF && index + 1 < text.length) {
    const secondCode = text.charCodeAt(index + 1)
    if (secondCode >= 0xDC00 && secondCode <= 0xDFFF) {
      return { bytes: 4, codeUnits: 2 }
    }
  }

  return { bytes: 3, codeUnits: 1 }
}

function utf8ByteLength (value) {
  const text = typeof value === 'string' ? value : ''
  let bytes = 0

  for (let index = 0; index < text.length;) {
    const character = utf8CharacterSize(text, index)
    bytes += character.bytes
    index += character.codeUnits
  }

  return bytes
}

function textIndexForUtf8Offset (text, targetOffset) {
  let bytes = 0
  let index = 0

  while (index < text.length && bytes < targetOffset) {
    const character = utf8CharacterSize(text, index)
    if (bytes + character.bytes > targetOffset) {
      return index
    }
    bytes += character.bytes
    index += character.codeUnits
  }

  return index
}

function normalizePageOffsets (text, pageOffsets) {
  if (!Array.isArray(pageOffsets) || pageOffsets.length === 0) {
    return []
  }

  const textByteLength = utf8ByteLength(text)
  const normalized = []
  let previous = -1

  for (const value of pageOffsets) {
    const offset = Number(value)
    if (!Number.isInteger(offset) || offset < 0 || offset > textByteLength || offset <= previous) {
      return []
    }
    normalized.push(offset)
    previous = offset
  }

  return normalized[0] === 0 ? normalized : []
}

function safeTextBoundary (text, boundary) {
  if (boundary <= 0 || boundary >= text.length) {
    return boundary
  }

  const previousCode = text.charCodeAt(boundary - 1)
  const nextCode = text.charCodeAt(boundary)
  const splitsSurrogatePair = previousCode >= 0xD800 && previousCode <= 0xDBFF &&
    nextCode >= 0xDC00 && nextCode <= 0xDFFF

  return splitsSurrogatePair ? boundary + 1 : boundary
}

function splitTextIntoEditorBlocks (value) {
  const text = typeof value === 'string' ? value : ''
  if (!text.length) {
    return ['']
  }

  const blocks = []
  let offset = 0

  while (offset < text.length) {
    let boundary = Math.min(offset + EDITOR_BLOCK_TARGET_LENGTH, text.length)

    if (boundary < text.length) {
      const minimumBoundary = offset + Math.floor(EDITOR_BLOCK_TARGET_LENGTH * 0.65)
      const candidateText = text.slice(minimumBoundary, boundary)
      const lineBreak = candidateText.lastIndexOf('\n')
      const whitespace = candidateText.search(/\s(?=\S*$)/)
      const relativeBoundary = lineBreak >= 0 ? lineBreak + 1 : (whitespace >= 0 ? whitespace + 1 : -1)

      if (relativeBoundary > 0) {
        boundary = minimumBoundary + relativeBoundary
      }
    }

    boundary = safeTextBoundary(text, boundary)
    blocks.push(text.slice(offset, boundary))
    offset = boundary
  }

  return blocks.join('') === text ? blocks : [text]
}

function splitTextIntoPageAwareEditorBlocks (value, pageOffsets) {
  const text = typeof value === 'string' ? value : ''
  const normalizedOffsets = normalizePageOffsets(text, pageOffsets)
  if (normalizedOffsets.length === 0) {
    return {
      blocks: splitTextIntoEditorBlocks(text),
      blockPageIndexes: [0],
      hasPageOffsets: false,
      pageCount: 0
    }
  }

  const pageIndexes = normalizedOffsets.map(offset => textIndexForUtf8Offset(text, offset))
  const blocks = []
  const blockPageIndexes = []

  pageIndexes.forEach((pageStart, pageIndex) => {
    const pageEnd = pageIndex + 1 < pageIndexes.length ? pageIndexes[pageIndex + 1] : text.length
    const pageBlocks = splitTextIntoEditorBlocks(text.slice(pageStart, pageEnd))
    pageBlocks.forEach(block => {
      blocks.push(block)
      blockPageIndexes.push(pageIndex)
    })
  })

  return {
    blocks: blocks.join('') === text ? blocks : [text],
    blockPageIndexes: blocks.join('') === text ? blockPageIndexes : [0],
    hasPageOffsets: blocks.join('') === text,
    pageCount: blocks.join('') === text ? normalizedOffsets.length : 0
  }
}

export default {
  name: 'app-ai-edit-document',
  props: {
    document: { type: Object, required: true },
    activeDomain: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      activeTab: 'metadata',
      savingMetadata: false,
      savingText: false,
      loadingText: false,
      global: !this.document.domain_id,
      textBlocks: [''],
      textBlockPageIndexes: [0],
      hasPageOffsets: false,
      pageCount: 0,
      currentBlockIndex: 0,
      originalText: '',
      contentRevision: this.document.content_revision !== undefined ? this.document.content_revision : null,
      loadedTextStatus: null,
      revisionConflict: false,
      chunksCount: this.document.chunks_count !== undefined ? this.document.chunks_count : null,
      form: {
        title: this.document.title || '',
        doc_type: this.document.doc_type || 'autre',
        priority: this.document.priority !== undefined && this.document.priority !== null ? this.document.priority : 100,
        signed_at: this.asDateObject(this.document.signed_at),
        valid_until: this.asDateObject(this.document.valid_until),
        source_url: this.document.source_url || ''
      },
      docTypeOptions: DOC_TYPE_OPTIONS
    }
  },
  computed: {
    documentDomain () {
      return this.document && this.document.domain ? this.document.domain : {}
    },
    scopedDomainUuid () {
      if (this.documentDomain.uuid) {
        return this.documentDomain.uuid
      }
      return this.activeDomain && this.activeDomain.uuid ? this.activeDomain.uuid : ''
    },
    scopedDomainLabel () {
      if (this.documentDomain.label) {
        return this.documentDomain.label
      }
      return this.activeDomain && this.activeDomain.label ? this.activeDomain.label : 'Domaine courant'
    },
    canSaveMetadata () {
      if (this.isTextLocked || this.revisionConflict || this.contentRevision === null) {
        return false
      }
      if (!this.form.title.trim()) {
        return false
      }
      if (!this.global && !this.scopedDomainUuid) {
        return false
      }
      return true
    },
    charCount () {
      return this.fullText.length
    },
    fullText () {
      return this.textBlocks.join('')
    },
    currentTextBlock: {
      get () {
        return this.textBlocks[this.currentBlockIndex] || ''
      },
      set (value) {
        this.$set(this.textBlocks, this.currentBlockIndex, value)
      }
    },
    documentStatus () {
      if (this.loadedTextStatus) {
        return this.loadedTextStatus
      }
      if (this.document && this.document.metadata && this.document.metadata.status) {
        return this.document.metadata.status
      }
      return this.document && this.document.status ? this.document.status : 'ready'
    },
    isTextLocked () {
      return this.documentStatus === 'queued' || this.documentStatus === 'processing'
    },
    currentPageNumber () {
      if (!this.hasPageOffsets) {
        return null
      }

      return this.currentPageIndex !== null ? this.currentPageIndex + 1 : null
    },
    currentPageIndex () {
      if (!this.hasPageOffsets) {
        return null
      }

      const pageIndex = this.textBlockPageIndexes[this.currentBlockIndex]
      return pageIndex !== undefined ? pageIndex : null
    },
    currentUnitBlockIndexes () {
      if (!this.hasPageOffsets) {
        return this.textBlocks[this.currentBlockIndex] !== undefined
          ? [this.currentBlockIndex]
          : []
      }

      if (this.currentPageIndex === null) {
        return []
      }

      return this.textBlockPageIndexes.reduce((indexes, pageIndex, blockIndex) => {
        if (pageIndex === this.currentPageIndex) {
          indexes.push(blockIndex)
        }
        return indexes
      }, [])
    },
    textAfterCurrentUnitDeletion () {
      const removedIndexes = new Set(this.currentUnitBlockIndexes)
      return this.textBlocks
        .filter((block, blockIndex) => !removedIndexes.has(blockIndex))
        .join('')
    },
    canDeleteCurrentUnit () {
      return !this.isTextLocked &&
        !this.revisionConflict &&
        this.contentRevision !== null &&
        this.currentUnitBlockIndexes.length > 0 &&
        this.textAfterCurrentUnitDeletion.trim().length > 0
    },
    deleteCurrentUnitLabel () {
      return this.hasPageOffsets ? 'Supprimer cette page' : 'Supprimer ce bloc'
    },
    deleteCurrentUnitTitle () {
      if (this.canDeleteCurrentUnit) {
        return this.hasPageOffsets
          ? 'Retirer toute cette page du texte qui sera réindexé'
          : 'Retirer ce bloc du texte qui sera réindexé'
      }

      return 'Le document doit conserver au moins une page ou un bloc contenant du texte.'
    },
    recalculatedPageOffsets () {
      if (!this.hasPageOffsets) {
        return []
      }

      const offsets = []
      let bytes = 0
      let previousPageIndex = null

      this.textBlocks.forEach((block, blockIndex) => {
        const pageIndex = this.textBlockPageIndexes[blockIndex]
        if (pageIndex !== previousPageIndex) {
          offsets.push(bytes)
          previousPageIndex = pageIndex
        }
        bytes += utf8ByteLength(block)
      })

      return offsets
    },
    pageOffsetsValid () {
      if (!this.hasPageOffsets) {
        return true
      }

      return this.recalculatedPageOffsets.every((offset, index, offsets) => {
        return index === 0 ? offset === 0 : offset > offsets[index - 1]
      })
    },
    canSaveText () {
      return !this.isTextLocked &&
        !this.revisionConflict &&
        this.pageOffsetsValid &&
        this.contentRevision !== null &&
        this.fullText.trim().length > 0 &&
        this.fullText !== this.originalText
    }
  },
  mounted () {
    this.loadText()
  },
  methods: {
    asDateObject (value) {
      if (!value) {
        return null
      }
      const date = value instanceof Date ? value : new Date(value)
      return isNaN(date.getTime()) ? null : date
    },
    asDate (value) {
      if (!value) {
        return null
      }
      const date = value instanceof Date ? value : new Date(value)
      return isNaN(date.getTime()) ? null : format(date, 'yyyy-MM-dd')
    },
    metadataPayload () {
      return {
        title: this.form.title.trim(),
        doc_type: this.form.doc_type || 'autre',
        priority: this.form.priority !== null && this.form.priority !== '' ? Number(this.form.priority) : 100,
        signed_at: this.asDate(this.form.signed_at),
        valid_until: this.asDate(this.form.valid_until),
        source_url: this.form.source_url || null,
        domain_uuid: this.global ? null : this.scopedDomainUuid
      }
    },
    saveMetadata () {
      if (!this.canSaveMetadata || this.savingMetadata) {
        return
      }
      this.savingMetadata = true
      aiService.updateDocument(
        this.document.id,
        this.metadataPayload(),
        this.contentRevision
      ).then(result => {
        this.$buefy.notification.open({
          duration: 3500,
          message: 'Le document a été mis à jour.',
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: false
        })
        this.$emit('saved', result)
        this.$parent.close()
      }).catch(err => {
        this.notifyMetadataError(err)
      }).finally(() => {
        this.savingMetadata = false
      })
    },
    loadText () {
      this.loadingText = true
      aiService.getDocumentText(this.document.id).then(data => {
        const text = typeof data.text === 'string' ? data.text : ''
        this.setEditorText(text, data.page_offsets)
        this.originalText = text
        this.contentRevision = data.content_revision !== undefined ? data.content_revision : null
        this.loadedTextStatus = data.status || null
        this.revisionConflict = false
        this.chunksCount = data.chunks_count !== undefined ? data.chunks_count : this.chunksCount
      }).catch(err => {
        this.notifyError(err, 'Impossible de charger le texte extrait.')
      }).finally(() => {
        this.loadingText = false
      })
    },
    reloadText () {
      this.loadText()
    },
    setEditorText (text, pageOffsets = []) {
      const editor = splitTextIntoPageAwareEditorBlocks(text, pageOffsets)
      this.textBlocks = editor.blocks
      this.textBlockPageIndexes = editor.blockPageIndexes
      this.hasPageOffsets = editor.hasPageOffsets
      this.pageCount = editor.pageCount
      this.currentBlockIndex = 0
    },
    previousBlock () {
      if (this.currentBlockIndex > 0) {
        this.currentBlockIndex -= 1
      }
    },
    nextBlock () {
      if (this.currentBlockIndex < this.textBlocks.length - 1) {
        this.currentBlockIndex += 1
      }
    },
    confirmDeleteCurrentUnit () {
      if (!this.canDeleteCurrentUnit) {
        return
      }

      const unitLabel = this.hasPageOffsets
        ? `la page ${this.currentPageNumber}`
        : `le bloc ${this.currentBlockIndex + 1}`
      this.$buefy.dialog.confirm({
        title: this.hasPageOffsets ? 'Supprimer la page' : 'Supprimer le bloc',
        message: `Voulez-vous retirer <b>${unitLabel}</b> du document ? Cette suppression sera appliquée à l’index après avoir cliqué sur <b>Enregistrer et reconstruire l’index</b>.`,
        type: 'is-danger',
        hasIcon: true,
        icon: 'exclamation-triangle',
        cancelText: 'Annuler',
        confirmText: 'Retirer',
        onConfirm: () => this.deleteCurrentUnit()
      })
    },
    deleteCurrentUnit () {
      if (!this.canDeleteCurrentUnit) {
        return
      }

      const removedIndexes = new Set(this.currentUnitBlockIndexes)
      const firstRemovedIndex = this.currentUnitBlockIndexes[0]
      const removedPageIndex = this.currentPageIndex
      const remainingBlocks = []
      const remainingPageIndexes = []

      this.textBlocks.forEach((block, blockIndex) => {
        if (removedIndexes.has(blockIndex)) {
          return
        }

        remainingBlocks.push(block)
        const pageIndex = this.textBlockPageIndexes[blockIndex] || 0
        remainingPageIndexes.push(
          this.hasPageOffsets && removedPageIndex !== null && pageIndex > removedPageIndex
            ? pageIndex - 1
            : pageIndex
        )
      })

      this.textBlocks = remainingBlocks
      this.textBlockPageIndexes = remainingPageIndexes
      if (this.hasPageOffsets) {
        this.pageCount = Math.max(1, this.pageCount - 1)
      }
      this.currentBlockIndex = Math.min(firstRemovedIndex, this.textBlocks.length - 1)
    },
    saveText () {
      if (!this.canSaveText || this.savingText) {
        return
      }

      const textToSave = this.fullText
      const pageOffsetsToSave = this.recalculatedPageOffsets
      this.savingText = true
      aiService.updateDocumentText(
        this.document.id,
        textToSave,
        this.contentRevision,
        pageOffsetsToSave
      ).then(result => {
        this.originalText = textToSave
        this.contentRevision = result.content_revision !== undefined
          ? result.content_revision
          : this.contentRevision
        this.chunksCount = result.chunks_count !== undefined ? result.chunks_count : this.chunksCount
        const status = this.resultStatus(result)
        this.loadedTextStatus = status
        this.$buefy.notification.open({
          duration: 5000,
          message: result.message || this.textSaveFallbackMessage(status),
          position: 'is-bottom-right',
          type: this.resultNotificationType(status),
          hasIcon: false
        })
        this.$emit('saved', result)
        this.$parent.close()
      }).catch(err => {
        this.notifyTextError(err)
      }).finally(() => {
        this.savingText = false
      })
    },
    docTypeLabel (type) {
      const option = DOC_TYPE_OPTIONS.find(option => option.value === type)
      return option ? option.text : type
    },
    notifyMetadataError (err) {
      const status = err && err.response ? err.response.status : null
      const errorCode = this.responseErrorCode(err)
      let message = 'Impossible de mettre à jour le document.'
      if (status === 403) {
        message = 'Vous n’avez pas le rôle requis pour modifier ce document.'
      } else if (status === 409) {
        if (errorCode === 'document_busy') {
          this.loadedTextStatus = 'processing'
        }
        message = err.response.data && err.response.data.message
          ? err.response.data.message
          : 'Le document a changé. Rechargez-le avant de recommencer.'
      } else if (status === 422) {
        const errors = err.response.data && err.response.data.errors
        message = errors ? Object.values(errors).flat().join(' ') : 'Champs invalides.'
      }
      this.notifyError(err, message)
    },
    notifyTextError (err) {
      const status = err && err.response ? err.response.status : null
      const responseData = err && err.response && err.response.data ? err.response.data : {}
      const errorCode = this.responseErrorCode(err)
      const revisionConflict = errorCode === 'revision_conflict' ||
        (!errorCode && responseData.expected_revision !== undefined && responseData.content_revision !== undefined)
      if (status === 409 && revisionConflict) {
        this.revisionConflict = true
      } else if (status === 409 && errorCode === 'document_busy') {
        this.loadedTextStatus = 'processing'
      }
      const responseMessage = responseData.message || null
      let fallbackMessage = 'Impossible d’enregistrer le texte extrait.'
      if (status === 409) {
        fallbackMessage = revisionConflict
          ? 'Le texte a été modifié ailleurs. Rechargez la dernière version avant de recommencer.'
          : 'Le document est déjà en cours d’indexation.'
      } else if (status === 422) {
        fallbackMessage = 'Le texte ou ses repères de pages sont invalides.'
      }
      const message = responseMessage || fallbackMessage
      this.notifyError(err, message)
    },
    responseErrorCode (err) {
      const data = err && err.response && err.response.data ? err.response.data : {}
      return data.code || data.error_code || null
    },
    resultStatus (result) {
      if (result && result.status) {
        return result.status
      }
      if (result && result.document && result.document.metadata) {
        return result.document.metadata.status || 'ready'
      }
      return 'ready'
    },
    textSaveFallbackMessage (status) {
      if (status === 'failed') {
        return 'Le texte a été sauvegardé, mais la reconstruction de l’index a échoué.'
      }
      return status === 'queued' || status === 'processing'
        ? 'Le texte a été sauvegardé. La reconstruction de l’index est en attente.'
        : 'Le texte a été sauvegardé et l’index est prêt.'
    },
    resultNotificationType (status) {
      if (status === 'ready') {
        return 'is-success'
      }
      return status === 'failed' ? 'is-danger' : 'is-info'
    },
    notifyError (err, fallback) {
      const status = err && err.response ? err.response.status : null
      this.$buefy.notification.open({
        duration: 5000,
        message: status === 403 ? 'Vous n’avez pas le rôle requis pour modifier ce document.' : fallback,
        position: 'is-bottom-right',
        type: 'is-danger',
        hasIcon: false
      })
    }
  }
}
</script>

<style scoped>
.ai-document-modal {
  width: min(980px, calc(100vw - 2rem));
  max-width: 100%;
}

.ai-document-modal-title {
  color: #111827;
  font-weight: 800;
}

.ai-document-modal-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.8rem;
}

.ai-document-tabs {
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.75rem 1rem 0;
}

.ai-document-tabs button {
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 800;
  padding: 0.6rem 0.8rem;
}

.ai-document-tabs button.is-active {
  border-bottom-color: #2563eb;
  color: #1d4ed8;
}

.ai-document-modal-body {
  max-height: 62vh;
  overflow-y: auto;
}

.ai-text-loading {
  min-height: 18rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 0.875rem;
}

.ai-text-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  font-size: 0.8rem;
  font-weight: 700;
}

.ai-text-notice--warning {
  border-color: #fcd34d;
  background: #fffbeb;
  color: #92400e;
}

.ai-text-notice--danger {
  border-color: #fca5a5;
  background: #fef2f2;
  color: #991b1b;
}

.ai-text-notice button {
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 800;
  text-decoration: underline;
  white-space: nowrap;
}

.ai-block-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #f8fafc;
  margin-bottom: 0.5rem;
  padding: 0.65rem;
}

.ai-block-position {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #334155;
  font-size: 0.8rem;
  text-align: center;
}

.ai-block-position span {
  color: #64748b;
  font-size: 0.7rem;
}

.ai-block-navigation {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.4rem;
  background: #ffffff;
  color: #334155;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.45rem 0.65rem;
}

.ai-block-navigation:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.ai-block-help {
  margin: 0 0 0.75rem;
  color: #64748b;
  font-size: 0.75rem;
}

.ai-block-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
}

.ai-block-delete {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #fca5a5;
  border-radius: 0.4rem;
  background: #ffffff;
  color: #b91c1c;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.45rem 0.65rem;
}

.ai-block-delete:hover:not(:disabled) {
  background: #fef2f2;
}

.ai-block-delete:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.ai-text-editor {
  width: 100%;
  min-height: min(52vh, 32rem);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.55;
}

@media (max-width: 640px) {
  .ai-document-tabs {
    flex-direction: column;
  }

  .ai-block-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .ai-block-navigation {
    justify-content: center;
  }

  .ai-text-notice {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
